import React, { useState } from 'react';
import { 
  Box, Typography, Button, Card, CardContent, Grid, TextField, Chip, 
  Avatar, Divider, Stack, IconButton, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert 
} from '@mui/material';
import { Agriculture, Edit, Delete, LocationOn, Phone, Badge } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

const Explotacion = () => {
  const queryClient = useQueryClient();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', codigo_rega: '', ubicacion: '', contacto: '', estado: 'activo' });

  const { data: explotacionesData, isLoading } = useQuery('explotaciones', async () => {
    const res = await axios.get('/api/explotaciones');
    return res.data;
  });

  const explotaciones = Array.isArray(explotacionesData) ? explotacionesData : [];

  const createMutation = useMutation(async (data) => await axios.post('/api/explotaciones', data), {
    onSuccess: () => { queryClient.invalidateQueries('explotaciones'); setDialogOpen(false); showMessage('✅ Explotación registrada'); }
  });

  const updateMutation = useMutation(async (data) => await axios.put(`/api/explotaciones/${data.id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries('explotaciones'); setDialogOpen(false); showMessage('✅ Cambios guardados'); }
  });

  const deleteMutation = useMutation(async (id) => await axios.delete(`/api/explotaciones/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('explotaciones'); showMessage('🗑️ Explotación eliminada'); }
  });

  const showMessage = (msg) => { setSnackbarMessage(msg); setOpenSnackbar(true); };

  const handleOpenDialog = (exp = null) => {
    if (exp) {
      setFormData(exp);
      setIsEditing(true);
    } else {
      setFormData({ nombre: '', codigo_rega: '', ubicacion: '', contacto: '', estado: 'activo' });
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  if (isLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Cargando centros...</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Gestión de Explotaciones</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>Administración de centros ganaderos y sedes oficiales.</Typography>
        </Box>
        <Button 
          className="luxury-button luxury-button-primary"
          variant="contained" 
          startIcon={<Badge />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Explotación
        </Button>
      </Box>

      <Grid container spacing={3}>
        {explotaciones.map((exp) => (
          <Grid item xs={12} md={6} lg={4} key={exp.id}>
            <Card className="premium-card">
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'var(--emerald-main)15', color: 'var(--emerald-main)' }}><Agriculture /></Avatar>
                  <Chip label={exp.estado} size="small" color={exp.estado === 'activo' ? 'success' : 'default'} sx={{ fontWeight: 800 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>{exp.nombre}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', display: 'block', mb: 2 }}>REGA: {exp.codigo_rega}</Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationOn sx={{ color: 'var(--slate-light)', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{exp.ubicacion || 'No definida'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Phone sx={{ color: 'var(--slate-light)', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{exp.contacto || 'Sin contacto'}</Typography>
                  </Box>
                </Stack>

                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button fullWidth variant="outlined" size="small" startIcon={<Edit />} onClick={() => handleOpenDialog(exp)} sx={{ borderRadius: '8px' }}>Editar</Button>
                  <IconButton color="error" onClick={() => { if(window.confirm('¿Eliminar esta explotación?')) deleteMutation.mutate(exp.id) }}><Delete fontSize="small" /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {explotaciones.length === 0 && (
          <Grid item xs={12}><Box sx={{ p: 8, textAlign: 'center', bgcolor: 'white', borderRadius: '24px', border: '2px dashed var(--border-color)' }}><Typography sx={{ color: 'var(--slate-light)' }}>No hay explotaciones registradas.</Typography></Box></Grid>
        )}
      </Grid>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEditing ? 'Editar Explotación' : 'Registrar Explotación'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre de la Explotación" value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código REGA" placeholder="ES48..." value={formData.codigo_rega} onChange={(e) => setFormData({...formData, codigo_rega: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={formData.estado} label="Estado" onChange={(e) => setFormData({...formData, estado: e.target.value})}>
                  <MenuItem value="activo">Activa</MenuItem>
                  <MenuItem value="inactivo">Inactiva</MenuItem>
                  <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Ubicación / Dirección" value={formData.ubicacion} onChange={(e) => setFormData({...formData, ubicacion: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Contacto (Tel/Email)" value={formData.contacto} onChange={(e) => setFormData({...formData, contacto: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => isEditing ? updateMutation.mutate(formData) : createMutation.mutate(formData)}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Explotacion;
